/**
 * DOM stage for Survey Says — same design-resolution pattern as WhatIs.
 *
 * Authored against a 1920x1080 base; scaled to the viewport via a uniform
 * CSS transform so OBS browser sources, dashboard previews, and Playwright
 * harnesses all render identically. Internal-to-survey-says copy (the
 * element ids are namespaced "ss-" so a host page that mounts both browser
 * sources doesn't collide).
 */

export const STAGE_W = 1920;
export const STAGE_H = 1080;

export interface DomStageHandle {
  el: HTMLDivElement;
  resize: () => void;
  dispose: () => void;
}

const BG_ID = 'ss-stage-bg';
const STAGE_ID = 'ss-stage';

function ensureBaseStyles(): void {
  if (document.getElementById('ss-stage-style')) return;
  const s = document.createElement('style');
  s.id = 'ss-stage-style';
  s.textContent = `
    #${BG_ID} {
      position: fixed;
      inset: 0;
      background: var(--sg-bg, #0A0E27);
      overflow: hidden;
      z-index: 0;
    }
    #${STAGE_ID} {
      position: fixed;
      left: 0;
      top: 0;
      width: ${STAGE_W}px;
      height: ${STAGE_H}px;
      transform-origin: 0 0;
      background: transparent;
      overflow: hidden;
      z-index: 1;
    }
  `;
  document.head.appendChild(s);
}

export interface DomStageOpts {
  mount?: HTMLElement;
}

export function createDomStage(opts: DomStageOpts = {}): DomStageHandle {
  ensureBaseStyles();
  const mount = opts.mount ?? document.body;

  const bg = document.createElement('div');
  bg.id = BG_ID;
  bg.setAttribute('data-overlay', 'stage-bg');
  mount.appendChild(bg);

  const el = document.createElement('div');
  el.id = STAGE_ID;
  el.setAttribute('data-overlay', 'stage');
  el.setAttribute('data-stage-w', String(STAGE_W));
  el.setAttribute('data-stage-h', String(STAGE_H));
  mount.appendChild(el);

  let lastScale = 0;
  let lastOffsetX = 0;
  let lastOffsetY = 0;

  function resize(): void {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const s = Math.min(vw / STAGE_W, vh / STAGE_H);
    const ox = Math.round((vw - STAGE_W * s) / 2);
    const oy = Math.round((vh - STAGE_H * s) / 2);
    if (s === lastScale && ox === lastOffsetX && oy === lastOffsetY) return;
    lastScale = s;
    lastOffsetX = ox;
    lastOffsetY = oy;
    el.style.transform = `translate(${ox}px, ${oy}px) scale(${s})`;
    (window as unknown as { __ss_stage_scale?: number }).__ss_stage_scale = s;
  }

  resize();
  window.addEventListener('resize', resize);
  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(() => resize());
    ro.observe(document.documentElement);
  }

  return {
    el,
    resize,
    dispose() {
      window.removeEventListener('resize', resize);
      if (ro) ro.disconnect();
      el.remove();
      bg.remove();
    },
  };
}
